/**
 * 활동 통계 카드
 */

import { useState, useEffect } from "react"
import Card from "@mui/material/Card"
import Grid from "@mui/material/Grid"
import Icon from "@mui/material/Icon"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import { request } from "app/network"

interface ActivityStatsProps {
    userId?: string
}

interface ActivityStats {
    createdCampaigns: number
    managedUsers: number
    processedPointRequests: number
    sentNotifications: number
    lastActivityTime: string | null
    totalActions: number
}

function ActivityStatsCard({ userId }: ActivityStatsProps): JSX.Element {
    const [stats, setStats] = useState<ActivityStats | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        loadStats()
    }, [])

    const loadStats = async () => {
        try {
            setLoading(true)
            const response = await request("User.getMyActivityStats", { paramz: {} })

            if (response.status === 200 && response.data) {
                setStats(response.data)
            }
        } catch (error) {
            console.error("활동 통계 로드 실패:", error)
        } finally {
            setLoading(false)
        }
    }

    const getTimeAgo = (dateString: string | null) => {
        if (!dateString) return "-"

        const date = new Date(dateString)
        const now = new Date()
        const diffMs = now.getTime() - date.getTime()
        const diffMins = Math.floor(diffMs / 60000)
        const diffHours = Math.floor(diffMins / 60)
        const diffDays = Math.floor(diffHours / 24)

        if (diffMins < 1) return "방금 전"
        if (diffMins < 60) return `${diffMins}분 전`
        if (diffHours < 24) return `${diffHours}시간 전`
        if (diffDays < 7) return `${diffDays}일 전`
        return date.toLocaleDateString("ko-KR")
    }

    if (loading) {
        return (
            <Card sx={{ height: "100%", boxShadow: "none" }}>
                <MDBox p={2}>
                    <MDTypography variant="body2" color="text">
                        로딩 중...
                    </MDTypography>
                </MDBox>
            </Card>
        )
    }

    return (
        <Card sx={{ height: "100%", boxShadow: "none" }}>
            <MDBox p={2}>
                <MDBox mb={2} display="flex" alignItems="center">
                    <Icon fontSize="medium" color="info">assessment</Icon>
                    <MDTypography variant="h6" fontWeight="medium" ml={1}>
                        내 활동 통계
                    </MDTypography>
                </MDBox>

                <Grid container spacing={2}>
                    {/* 총 활동 수 */}
                    <Grid item xs={12}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center">
                            <MDBox display="flex" alignItems="center">
                                <Icon fontSize="small" sx={{ mr: 1 }}>trending_up</Icon>
                                <MDTypography variant="button" fontWeight="regular" color="text">
                                    총 활동 수
                                </MDTypography>
                            </MDBox>
                            <MDTypography variant="button" fontWeight="bold" color="info">
                                {stats?.totalActions || 0}건
                            </MDTypography>
                        </MDBox>
                    </Grid>

                    {/* 발송한 알림 */}
                    <Grid item xs={12}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center">
                            <MDBox display="flex" alignItems="center">
                                <Icon fontSize="small" sx={{ mr: 1 }}>notifications</Icon>
                                <MDTypography variant="button" fontWeight="regular" color="text">
                                    발송한 알림
                                </MDTypography>
                            </MDBox>
                            <MDTypography variant="button" fontWeight="bold" color="text">
                                {stats?.sentNotifications || 0}건
                            </MDTypography>
                        </MDBox>
                    </Grid>

                    {/* 관리 중인 사용자 */}
                    <Grid item xs={12}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center">
                            <MDBox display="flex" alignItems="center">
                                <Icon fontSize="small" sx={{ mr: 1 }}>people</Icon>
                                <MDTypography variant="button" fontWeight="regular" color="text">
                                    관리 중인 사용자
                                </MDTypography>
                            </MDBox>
                            <MDTypography variant="button" fontWeight="bold" color="text">
                                {stats?.managedUsers || 0}명
                            </MDTypography>
                        </MDBox>
                    </Grid>

                    {/* 처리한 요청 */}
                    <Grid item xs={12}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center">
                            <MDBox display="flex" alignItems="center">
                                <Icon fontSize="small" sx={{ mr: 1 }}>check_circle</Icon>
                                <MDTypography variant="button" fontWeight="regular" color="text">
                                    처리한 요청
                                </MDTypography>
                            </MDBox>
                            <MDTypography variant="button" fontWeight="bold" color="text">
                                {stats?.processedPointRequests || 0}건
                            </MDTypography>
                        </MDBox>
                    </Grid>

                    {/* 최근 활동 */}
                    <Grid item xs={12}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center">
                            <MDBox display="flex" alignItems="center">
                                <Icon fontSize="small" sx={{ mr: 1 }}>schedule</Icon>
                                <MDTypography variant="button" fontWeight="regular" color="text">
                                    최근 활동
                                </MDTypography>
                            </MDBox>
                            <MDTypography variant="button" fontWeight="bold" color="success">
                                {getTimeAgo(stats?.lastActivityTime || null)}
                            </MDTypography>
                        </MDBox>
                    </Grid>
                </Grid>
            </MDBox>
        </Card>
    )
}

export default ActivityStatsCard
